/**
 * Utility functions.
 */

// Media baking utilities
export { bakeImage, bakeVideo, bakeCapture } from './mediaBaking';
export { buildOverlays, overlaysField, serializeOverlays } from './overlayPayload';
export { renderOverlays, hasOverlays, OVERLAY_GEOM } from './overlayRender';

// Network utilities
export { fetchWithRetry } from './fetchWithRetry';
export type { FetchWithRetryOptions } from './fetchWithRetry';

// Scripture utilities
export { BIBLE_BOOKS, parseScriptureRef } from './scriptureUtils';

// Highlight colors
export { HIGHLIGHT_COLORS, HIGHLIGHT_COLOR_NAMES, getHighlightBg, getHighlightSolid } from './highlightColors';
export type { HighlightColorName } from './highlightColors';

/**
 * Time-based greeting (morning/afternoon/evening).
 */
export function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

/**
 * Format a date for display (e.g., "January 15, 2026").
 */
export function formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions): string {
  return new Date(date).toLocaleDateString('en-US', options ?? { month: 'long', day: 'numeric', year: 'numeric' });
}

/**
 * Format a date as a separator label (Today/Yesterday/date).
 */
export function formatDateSeparator(date: string | Date): string {
  const d = new Date(date);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.floor((today.getTime() - target.getTime()) / 86400000);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return d.toLocaleDateString('en-US', { weekday: 'long' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Format a date relative to now (e.g., "2 hours ago").
 */
export function formatRelativeTime(date: string | Date): string {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;

  return then.toLocaleDateString();
}

/**
 * Truncate text to a maximum length with ellipsis.
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

/**
 * Generate initials from a name.
 */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

